// @flow
import React from 'react';
import  styled  from '@emotion/styled';
import { colors } from '@atlaskit/theme';
import { borderRadius, grid } from '../constants';
import type { Quote, AuthorColors } from '../types';
import type { DraggableProvided } from 'react-beautiful-dnd';
import ReactTags from 'react-tag-autocomplete';
import "./../css/tagInput.css";
import CardService from "./../../services/card.js";
import {getBoardIdFromURL} from "./../../util/common.js";

type Props = {
  card: Quote,
  isDragging: boolean,
  provided: DraggableProvided,
  isClone?: boolean,
  isGroupedOver?: boolean,
  style?: Object,
  index?: number,
};

const getBackgroundColor = (
  isDragging: boolean,
  isGroupedOver: boolean,
  authorColors: AuthorColors,
) => {
  if (isDragging) {
    return authorColors.soft;
  }

  if (isGroupedOver) {
    return "#f7f5f4";
  }

  return colors.N0;
};

const getBorderColor = (isDragging: boolean, authorColors: AuthorColors) =>
  isDragging ? authorColors?.hard : 'transparent';

const imageSize: number = 40;
 
const Container = styled.a`
  border-radius: ${borderRadius}px;
  border: 2px solid transparent;
  border-color: ${(props) => getBorderColor(props.isDragging, props.colors)};
  background-color: ${(props) =>
    getBackgroundColor(props.isDragging, props.isGroupedOver, props.colors)};
  box-shadow: ${({ isDragging }) =>
    isDragging ? `2px 2px 1px ${colors.N70}` : 'none'};
  box-sizing: border-box;
  padding: ${grid}px;
  min-height: ${imageSize}px;
  margin-bottom: 15px;
  user-select: none;
  box-shadow: 1px 2px 5px 0px rgb(0 0 0 / 4%);
  /* anchor overrides */
  color: ${colors.N900};
  font-family: 'Segoe UI Regular';
  &:hover,
  &:active {
    color: ${colors.N900};
    text-decoration: none;
  }

  &:focus {
    outline: none;
    border-color: ${(props) => props.colors.hard};
    box-shadow: none;
  }

  /* flexbox */
  display: block;
`;

const Content = styled.div`
  /* flex child */
  flex-grow: 1;

  /*
    Needed to wrap text in ie11
    https://stackoverflow.com/questions/35111090/why-ie11-doesnt-wrap-the-text-in-flexbox
  */
  flex-basis: 100%;
 `;

const CardText = styled.div`
  font-weight: 500;
  flex: 1;
  padding-right: 7px;
`;

const TextArea = styled.textarea`
  font-family: 'Segoe UI Regular';
  width: 100%;
  height: ${props => props.height ? props.height : "120"}px;
  font-weight: 500;
  font-size: 14px;
 
  overflow: auto;
  resize: none;

  /* boarder */
  outline: none !important;
  outline-color: #D1D1D1 !important;
  /*border-style: none;*/
  border-color: #D1D1D1;
  

  /* cursor color*/
  caret-color: blue;
 
`;

const TagsInputWraper = styled.div`
  width: 100%; 
`;

const ActionButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
`;

const Button = styled.button`
  padding: 6px;
  padding-left: 10px;
  padding-right: 10px;
  background-color: ${props => props.color ? props.color : "#0052CC"};
  color: white;
  font-weight: 500;
  border: none;
  margin: 5px;
  margin-top: 9px;
  margin-bottom: 9px;
  cursor: pointer;
  &:hover {
    background-color: ${props => props.hoverColor ? props.hoverColor : '#0065FF'};
  }
`;
 
const Errors = styled.div`
  width: 100%;
  padding-top: 10px;
  padding-bottom: 10px;
`;

const Error = styled.div`
  color: red;
  
`;

function getStyle(provided: DraggableProvided, style: ?Object) {
  if (!style) {
    return provided?.draggableProps?.style;
  }

  return {
    ...provided?.draggableProps?.style,
    ...style,
  };
}

function getRandomTagColor(arrayOfValuesToExclude) {
  const availableColors = [
    "#FF9056",
    "#FFBF56",
    "#56FFB4",
    "#56DFFF",
    "#56A6FF",
    "#567DFF",
    "#8256FF",
    "#BE56FF",
    "#FF56B9",
    "#FF5668",
    "#40A830",
    "#CB423B",
    "#971D9B",
    "#DB6576",
    "#D45182",
    "#DC6161",
    "#FF5B56",
    "#5661ff",
    "#ff9e56",
    "#1aae27"];

  // TODO: check if tag already exist in ANY card in the board, give it same color. (maybe maintain list of tags in an object for easy reads)
  let count = 0;
  const maxTries = 20;
  let randomValue = null;
  while (count < maxTries) {
    randomValue = availableColors[Math.floor(Math.random() * availableColors.length)];
    // if the color is not reserved for another exiting tag, return it
    if (!arrayOfValuesToExclude.includes(randomValue)) {
      return randomValue;
    }
    count++;
  }
  return '#FF5B56'; // default color for worst case (when there are 20 tags on the board and there are no more colors to choose from.)
}

// Previously this extended React.Component
// That was a good thing, because using React.PureComponent can hide
// issues with the selectors. However, moving it over does can considerable
// performance improvements when reordering big lists (400ms => 200ms)
// Need to be super sure we are not relying on PureComponent here for
// things we should be doing in the selector as we do not know if consumers
// will be using PureComponent
export default class CardEdit extends React.PureComponent {
  constructor(props) {
    super(props);
    const { mapOfAllTags, card = null } = this.props;
    const arrayOfAllBoardTags = Array.from(mapOfAllTags?.keys()?? [])?.map((tagText, index) => ({id: index, name: tagText}))
    const cardTags = card?.tags?.length ? card.tags.map((tagObject, index) => ({id: index, name: tagObject.text})) : [];
    this.state = {
      content: card?.content,
      tags: cardTags,
      tagSuggestions: arrayOfAllBoardTags ?? [],
      errors: new Set()
    }
    this._textareaElement = null;
    this.reactTags = React.createRef();
  }
  
  componentDidMount(){
    this._textareaElement.focus();
  }

  onDelete = (i) => {
    const tags = this.state.tags.slice(0);
    tags.splice(i, 1);
    this.setState({ tags });
  }

  onAddition = (tag) => {
    // 1- check if tag already exist in this board, then get the color of it to set it here

    // 2- if this exist in the state.tags, that mean the user is duplicating a tag so prevent him

    // 3- otherwise, it means this is a new tag and we should generate a new color for it.
    const tags = [].concat(this.state.tags, tag);
    this.setState({ tags });
  }

  onSave = async () => {
    const {tags, content, errors} = this.state;
    const {card = null, mapOfAllTags, onUpdateSuccess, columnId} = this.props;
    const allColors = Array.from(mapOfAllTags.values()) ?? [];
    const newTags = tags.map((tagObject) => {
      return ({
        text: tagObject.name,
        color: mapOfAllTags.has(tagObject.name) ? mapOfAllTags.get(tagObject.name) : getRandomTagColor(allColors)
      });
    });
    if (!card) {
      // Means we're in create mode - create new card
      if (!columnId) {
        throw new Error("Column Id is undefined. Can't create new card :/ ");
      }
      if (!content) {
        this.setState({ errors: new Set([...errors, "Add some content"])});
        return;
      }
      await CardService.create(getBoardIdFromURL(), columnId, {
        content,
        tags: newTags
      });
    } else {
      // Means we're in edit mode - update card
      if (!content) {
        this.setState({ errors: new Set([...errors, "Add some content"])});
        return;
      }
      await CardService.update(card.boardId, card.columnId, card.cardId, {
        content,
        tags: newTags
      });
    }
    
    // propagate new data to parent card so it shows the new content immediately
    this.setState({ errors: new Set()});
    onUpdateSuccess(content, newTags);
  }
  
  onCancel =  () => {
    this.setState({ errors: new Set()});
    this.props.onCancel();
  }
  
  onValidate = (newTag) => {
    const {tags, errors} = this.state;
    // 1- Tag text shouldn't exceed 10 chars
    if (newTag.name?.trim()?.length === 0 || newTag.name?.length > 15) {
      this.setState({ errors: new Set([...errors, `A tag name should be less than 15 characters. "${newTag.name?.trim()}" has ${newTag.name?.trim()?.length}`])});
      return false;
    }
    // 2- When user types or selects a tag that already exist for this card, block him.
    const tagTextsArr = tags.map((tagObject) => tagObject.name);
    if (tagTextsArr.includes(newTag.name)){
      this.setState({ errors: new Set([...errors, `${newTag.name} is already added to this card`])});
      return false
    }
    return true;
  }

  render() {
    const {
      card,
      isDragging,
      isGroupedOver,
      provided,
      style,
      isClone,
      index,
      contentHeight
    } = this.props;
     const oneColor = {
      "soft": "#E3FCEF",
      "hard": "rgba(9, 30, 66, 0.71)"
    };
    const {errors} = this.state;

     return (
      <Container
        isDragging={isDragging}
        isGroupedOver={isGroupedOver}
        colors={oneColor}
        isClone={isClone}
        ref={provided?.innerRef}
        {...provided?.draggableProps}
        {...provided?.dragHandleProps}
        role={undefined}
        tabIndex={undefined}
        aria-describedby={undefined}
        style={getStyle(provided, style)}
        data-is-dragging={isDragging}
        data-testid={card?.id}
        data-index={index}
      >        
        <Content>
          {errors?.size > 0 && (
           <Errors>
              {Array.from(errors).map((error) => <Error key={error}>{error}</Error>)}
           </Errors>
          )}
           <CardText>
              <TextArea height={contentHeight} placeholder="Type in your thoughts here..." aria-label="Card content" ref={c => (this._textareaElement = c)} value={this.state.content} onChange={(e) => this.setState({content: e.target.value })} />
           </CardText>
           <TagsInputWraper>
            <ReactTags
                allowNew
                minQueryLength={1}
                maxSuggestionsLength={5}
                newTagText='Create new tag:'
                ref={this.reactTags}
                tags={this.state.tags}
                suggestions={this.state.tagSuggestions}
                onDelete={this.onDelete}
                onAddition={this.onAddition}
                onValidate={this.onValidate}
                placeholderText={'Add new tag'}
              />
           </TagsInputWraper>
           <ActionButtons>            
              <Button type="button" color="#8e8f91" hoverColor="#acaeb1" onClick={this.onCancel} aria-label={card ? 'Cancel editing card' : 'Cancel adding new card'}>Cancel</Button>
              <Button type="button" color="#0052CC" hoverColor="#0065FF" onClick={this.onSave} aria-label={card ? 'Save card' : 'Create card'}>Save</Button>
           </ActionButtons>
        </Content>        
      </Container>
    );
  }
}